<template>
  <div class="driver-order-list-page">
    <div class="page-header">
      <div class="header-left">
        <el-button type="text" icon="el-icon-arrow-left" @click="$router.back()" />
        <h1>📦 {{ $t('driverOrderList.title') }}</h1>
      </div>
    </div>

    <!-- 搜索栏 -->
    <div class="search-bar">
      <input v-model="keyword" :placeholder="$t('driverOrderList.searchPlaceholder')" class="search-input" @keyup.enter="loadOrders" />
      <button class="search-btn" @click="loadOrders">🔍 {{ $t('common.search') }}</button>
    </div>

    <!-- 状态筛选 -->
    <div class="filter-tabs">
      <div :class="['tab', { active: statusFilter === '' }]" @click="statusFilter = ''; loadOrders()">{{ $t('common.all') }}</div>
      <div :class="['tab', { active: statusFilter === 'confirmed' }]" @click="statusFilter = 'confirmed'; loadOrders()">{{ $t('driverOrderList.statusConfirmed') }}</div>
      <div :class="['tab', { active: statusFilter === 'dispatching' }]" @click="statusFilter = 'dispatching'; loadOrders()">{{ $t('driverOrderList.statusDispatching') }}</div>
      <div :class="['tab', { active: statusFilter === 'delivered' }]" @click="statusFilter = 'delivered'; loadOrders()">{{ $t('driverOrderList.statusDelivered') }}</div>
    </div>

    <!-- 订单列表 -->
    <div class="order-list">
      <div v-if="loading" class="loading-state"><p>{{ $t('common.loading') }}</p></div>
      <div v-else-if="orders.length === 0" class="empty-state"><p>{{ $t('common.noRecord') }}</p></div>
      <div v-else>
        <div v-for="order in orders" :key="order.id" class="order-card" @click="$router.push('/delivery/orders/' + order.id)">
          <div class="order-header">
            <span class="order-no">{{ order.orderNo }}</span>
            <el-tag :type="getOrderStatusType(order.status)" size="small">{{ getOrderStatusText(order.status) }}</el-tag>
          </div>
          <div class="order-body">
            <div>👤 {{ order.customerName || '-' }}</div>
            <div>📞 {{ order.customerPhone || '-' }}</div>
            <div>💰 S$ {{ formatAmount(order.totalAmount) }}</div>
            <div>🕒 {{ formatDate(order.createdAt) }}</div>
          </div>
          <div class="order-footer">
            <router-link :to="'/delivery/orders/' + order.id" class="detail-link">{{ $t('driverOrderList.viewDetail') }}</router-link>
          </div>
        </div>
      </div>
    </div>

    <!-- 分页 -->
    <div v-if="total > 0" class="pagination">
      <button :disabled="currentPage <= 0" @click="goPage(currentPage - 1)">‹ {{ $t('driverOrderList.prevPage') }}</button>
      <span class="page-info">{{ currentPage + 1 }} / {{ totalPages }}</span>
      <button :disabled="currentPage >= totalPages - 1" @click="goPage(currentPage + 1)">{{ $t('driverOrderList.nextPage') }}</button>
      <span class="page-total">{{ $t('driverOrderList.totalCount') }} {{ total }} {{ $t('driverOrderList.items') }}</span>
    </div>
  </div>
</template>

<script>
import api from '@/services/api';

export default {
  name: 'DriverOrderList',
  data() {
    return {
      loading: false,
      orders: [],
      keyword: '',
      statusFilter: '',
      currentPage: 0,
      pageSize: 20,
      total: 0,
      totalPages: 0
    };
  },
  created() {
    this.loadOrders();
  },
  methods: {
    async loadOrders() {
      this.loading = true;
      try {
        const wholesalerId = localStorage.getItem('wholesaler_id') ||
          (JSON.parse(localStorage.getItem('driver_info') || '{}')).wholesalerId;
        const driverId = localStorage.getItem('driver_id');
        const params = { page: this.currentPage, size: this.pageSize, sourceChannel: 'DRIVER' };
        if (wholesalerId) params.wholesalerId = Number(wholesalerId);
        if (driverId) params.driverId = Number(driverId);
        if (this.keyword) params.keyword = this.keyword;
        if (this.statusFilter) params.status = this.statusFilter;
        const res = await api.get('/orders', { params });
        const data = Array.isArray(res) ? res : (res.items || res.content || res.data?.items || res.data?.content || []);
        this.orders = data;
        this.total = res.total || 0;
        this.totalPages = res.totalPages || 0;
      } catch (e) {
        this.$message.error(this.$t('driverOrderList.loadFailed'));
      } finally {
        this.loading = false;
      }
    },
    goPage(page) {
      if (page < 0 || page >= this.totalPages) return;
      this.currentPage = page;
      this.loadOrders();
      window.scrollTo(0, 0);
    },
    formatAmount(v) { return Number(v || 0).toFixed(2); },
    formatDate(d) { if (!d) return '-'; return new Date(d).toLocaleString(this.$i18n.locale, { month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit' }); },
    getOrderStatusType(s) {
      const m = { confirmed: 'warning', dispatching: 'success', delivered: 'info', COMPLETED: 'info' };
      return m[s] || 'info';
    },
    getOrderStatusText(s) {
      const m = {
        confirmed: 'driverOrderList.statusConfirmed',
        dispatching: 'driverOrderList.statusDispatching',
        delivered: 'driverOrderList.statusDelivered',
        COMPLETED: 'driverOrderList.statusCompleted'
      };
      const key = m[s];
      return key ? this.$t(key) : (s || '-');
    }
  }
};
</script>

<style lang="scss" scoped>
.driver-order-list-page { min-height: 100vh; background: #f5f7fa; padding-bottom: 40px; }
.page-header { background: white; padding: calc(16px + env(safe-area-inset-top, 0px)) 20px; display: flex; align-items: center; gap: 12px; }
.page-header h1 { font-size: 18px; margin: 0; }

.search-bar { display: flex; padding: 12px 16px; background: white; gap: 8px; }
.search-input { flex: 1; padding: 10px 14px; border: 1px solid #ddd; border-radius: 6px; font-size: 14px; }
.search-btn { padding: 10px 20px; background: #059669; color: white; border: none; border-radius: 6px; font-size: 14px; cursor: pointer; }

.filter-tabs { display: flex; background: white; padding: 12px 16px; gap: 8px; overflow-x: auto; }
.tab { padding: 8px 16px; background: #f5f7fa; border-radius: 20px; cursor: pointer; white-space: nowrap; font-size: 13px; }
.tab.active { background: #059669; color: white; }

.order-list { padding: 16px; }
.order-card { background: white; border-radius: 12px; padding: 16px; margin-bottom: 12px; cursor: pointer; transition: all 0.2s; }
.order-card:hover { box-shadow: 0 2px 12px rgba(0,0,0,0.1); }
.order-header { display: flex; justify-content: space-between; align-items: center; margin-bottom: 12px; }
.order-no { font-weight: 600; color: #059669; font-size: 14px; }
.order-body { font-size: 13px; color: #666; }
.order-body > div { margin-bottom: 6px; }
.order-footer { margin-top: 12px; text-align: right; }
.detail-link { color: #059669; font-size: 13px; text-decoration: none; }

.loading-state, .empty-state { text-align: center; padding: 60px; color: #999; }

.pagination { display: flex; justify-content: center; align-items: center; gap: 12px; padding: 16px; }
.pagination button { padding: 8px 16px; border: 1px solid #ddd; border-radius: 6px; background: white; cursor: pointer; font-size: 14px; }
.pagination button:disabled { opacity: 0.4; cursor: not-allowed; }
.pagination button:not(:disabled):hover { background: #059669; color: white; border-color: #059669; }
.page-info { font-weight: 600; color: #333; }
.page-total { color: #999; font-size: 13px; }
</style>
